import { useAuthenticatedFetch } from "@stardeck-customer-apps/project-auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { Expo48Data } from "@/server/db/expo-types";
import type { Registrations } from "@/server/db/types.db";
import type { PropertyPurchaseResponse } from "@/server/types";

import { queryKeys } from "./query-keys";

// DB type with timestamps as strings and expo_data parsed as expoData
type RegistrationItem = Omit<
  Registrations,
  | "expo_data"
  | "expo_version"
  | "id"
  | "uuid"
  | "legacy_uid"
  | "created_at"
  | "coupon_ready_at"
  | "coupons_printed_at"
  | "received_gift_at"
  | "remarks"
  | "gender"
> & {
  id: number;
  uuid: string;
  legacy_uid: string | null;
  expo_version: string;
  created_at: string;
  coupon_ready_at: string | null;
  coupons_printed_at: string | null;
  received_gift_at: string | null;
  remarks: string | null;
  gender: string | null;
  expoData: Expo48Data;
  property_purchases?: PropertyPurchaseResponse[];
};

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface RegistrationDetailResponse {
  item: RegistrationItem;
}

interface RegistrationListResponse {
  items: RegistrationItem[];
  pagination: PaginationInfo;
}

interface RegistrationListParams {
  page?: number;
  limit?: number;
  search?: string;
}

export function useRegistrationDetail(id: string | number | undefined) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: queryKeys.registrations.detail(id ?? ""),
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/admin/registrations/${id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch registration: ${response.statusText}`);
      }

      return response.json() as Promise<RegistrationDetailResponse>;
    },
    enabled: !!id,
  });
}

export function useRegistrationList(params: RegistrationListParams = {}) {
  const authenticatedFetch = useAuthenticatedFetch();
  const { page = 1, limit = 50, search = "" } = params;

  return useQuery({
    queryKey: queryKeys.registrations.list({ page, limit, search }),
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (search) {
        searchParams.append("search", search);
      }

      const response = await authenticatedFetch(
        `/api/admin/registrations?${searchParams.toString()}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch registrations: ${response.statusText}`);
      }

      return response.json() as Promise<RegistrationListResponse>;
    },
  });
}

interface StatsResponse {
  totalRegistrations: number;
  todayRegistrations: number;
  giftsDistributed: number;
  couponsReady: number;
  totalSales: number;
  todaySales: number;
}

export function useRegistrationStats(
  range: "today" | "yesterday" | "24h" | "3d" | "7d" | "30d" | "90d" | "expo48" = "7d"
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: [...queryKeys.registrations.stats, range],
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/admin/stats?range=${range}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch registration stats: ${response.statusText}`);
      }

      return response.json() as Promise<StatsResponse>;
    },
  });
}

interface TimelineDataPoint {
  time_period: string;
  registrations: number;
  coupons_ready: number;
  coupons_printed: number;
  gifts_distributed: number;
}

interface TimelineResponse {
  timeline: TimelineDataPoint[];
  granularity: string;
}

export function useRegistrationTimeline(
  range: "today" | "yesterday" | "24h" | "3d" | "7d" | "30d" | "90d" | "expo48" = "7d",
  granularity: "daily" | "hourly" = "daily"
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: [...queryKeys.registrations.stats, "timeline", range, granularity],
    queryFn: async () => {
      // Get timezone offset in minutes (e.g., -420 for PDT, 420 for Bangkok)
      const timezoneOffset = new Date().getTimezoneOffset();
      // Note: getTimezoneOffset returns positive for west of UTC, negative for east
      // We need to negate it for the API (e.g., Bangkok UTC+7 should be 420)
      const tzOffset = -timezoneOffset;

      const response = await authenticatedFetch(
        `/api/admin/stats/timeline?range=${range}&granularity=${granularity}&tzOffset=${tzOffset}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch timeline data: ${response.statusText}`);
      }

      return response.json() as Promise<TimelineResponse>;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

export function useUpdateRegistrationGift() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, received_gift }: { id: string | number; received_gift: boolean }) => {
      const response = await authenticatedFetch(`/api/admin/registrations/${id}/gift`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ received_gift }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update gift status: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.stats });
    },
  });
}

export function useMarkCouponsPrinted() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string | number }) => {
      const response = await authenticatedFetch(`/api/admin/registrations/${id}/print-coupons`, {
        method: "PATCH",
      });

      if (!response.ok) {
        throw new Error(`Failed to mark coupons as printed: ${response.statusText}`);
      }

      return response.json() as Promise<{
        ok: boolean;
        coupons: Array<{
          uuid: string;
          couponNumber: number;
        }>;
      }>;
    },
    onSuccess: (_data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
    },
  });
}

export function useUpdateRegistrationRemarks() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, remarks }: { id: string | number; remarks: string | null }) => {
      const response = await authenticatedFetch(`/api/admin/registrations/${id}/remarks`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ remarks }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update remarks: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: (_data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
    },
  });
}

interface UpdateRegistrationParams {
  id: string | number;
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  age?: number | null;
  gender?: string | null;
  interests?: string[];
  interestOther?: string | null;
  banks?: string[];
  propertyCharacteristics?: string | null;
  priceRange?: string | null;
  locationData?: { province: string; district?: string } | null;
  purchaseYear?: string | null;
  bankOther?: string | null;
}

export function useUpdateRegistration() {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateRegistrationParams) => {
      // Transform flat data structure into API format with expoData
      const {
        interests,
        interestOther,
        banks,
        bankOther,
        propertyCharacteristics,
        priceRange,
        locationData,
        purchaseYear,
        ...basicFields
      } = data;

      const payload: Record<string, unknown> = { ...basicFields };

      // Build expoData object from individual fields
      const expoData: Record<string, unknown> = {};
      if (interests !== undefined) expoData.interests = interests;
      if (interestOther !== undefined) expoData.interestOther = interestOther;
      if (banks !== undefined) expoData.banks = banks;
      if (bankOther !== undefined) expoData.bankOther = bankOther;
      if (propertyCharacteristics !== undefined)
        expoData.propertyCharacteristics = propertyCharacteristics;
      if (priceRange !== undefined) expoData.priceRange = priceRange;
      if (locationData !== undefined) expoData.locationData = locationData;
      if (purchaseYear !== undefined) expoData.purchaseYear = purchaseYear;

      if (Object.keys(expoData).length > 0) {
        payload.expoData = expoData;
      }

      const response = await authenticatedFetch(`/api/registrations/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to update registration: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.detail() });
      queryClient.invalidateQueries({ queryKey: ["registrations", "list"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.registrations.stats });
    },
  });
}

// User-level hooks (no PII access)

export function useUserStats(
  range: "today" | "yesterday" | "24h" | "3d" | "7d" | "30d" | "90d" | "expo48" = "7d"
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: [...queryKeys.user.stats, range],
    queryFn: async () => {
      const response = await authenticatedFetch(`/api/user/stats?range=${range}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch user stats: ${response.statusText}`);
      }

      return response.json() as Promise<StatsResponse>;
    },
  });
}

export function useUserTimeline(
  range: "today" | "yesterday" | "24h" | "3d" | "7d" | "30d" | "90d" | "expo48" = "7d",
  granularity: "daily" | "hourly" = "daily"
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: [...queryKeys.user.stats, "timeline", range, granularity],
    queryFn: async () => {
      // Get timezone offset in minutes (e.g., -420 for PDT, 420 for Bangkok)
      const timezoneOffset = new Date().getTimezoneOffset();
      // Note: getTimezoneOffset returns positive for west of UTC, negative for east
      // We need to negate it for the API (e.g., Bangkok UTC+7 should be 420)
      const tzOffset = -timezoneOffset;

      const response = await authenticatedFetch(
        `/api/user/stats/timeline?range=${range}&granularity=${granularity}&tzOffset=${tzOffset}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch user timeline data: ${response.statusText}`);
      }

      return response.json() as Promise<TimelineResponse>;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

interface BankStatsResponse {
  banks: Array<{ name: string; value: number }>;
}

export function useUserBankStats() {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: queryKeys.user.banks,
    queryFn: async () => {
      const response = await authenticatedFetch("/api/user/stats/banks");

      if (!response.ok) {
        throw new Error(`Failed to fetch user bank stats: ${response.statusText}`);
      }

      return response.json() as Promise<BankStatsResponse>;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Consolidated dashboard hooks

interface DashboardResponse {
  stats: StatsResponse;
  timeline: Array<{
    time_period: string;
    registrations: number;
    coupons_ready: number;
    coupons_printed: number;
    gifts_distributed: number;
  }>;
  timeLabel: string;
  granularity: string;
  banks: Array<{ name: string; value: number }>;
}

export function useAdminDashboard(
  range: "today" | "yesterday" | "24h" | "3d" | "7d" | "30d" | "90d" | "expo48" = "7d",
  granularity: "daily" | "hourly" = "daily"
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: [...queryKeys.admin.stats, "dashboard", range, granularity],
    queryFn: async () => {
      const timezoneOffset = new Date().getTimezoneOffset();
      const tzOffset = -timezoneOffset;

      const response = await authenticatedFetch(
        `/api/admin/dashboard?range=${range}&granularity=${granularity}&tzOffset=${tzOffset}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch admin dashboard: ${response.statusText}`);
      }

      return response.json() as Promise<DashboardResponse>;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

export function useUserDashboard(
  range: "today" | "yesterday" | "24h" | "3d" | "7d" | "30d" | "90d" | "expo48" = "7d",
  granularity: "daily" | "hourly" = "daily"
) {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: [...queryKeys.user.stats, "dashboard", range, granularity],
    queryFn: async () => {
      const timezoneOffset = new Date().getTimezoneOffset();
      const tzOffset = -timezoneOffset;

      const response = await authenticatedFetch(
        `/api/user/dashboard?range=${range}&granularity=${granularity}&tzOffset=${tzOffset}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch user dashboard: ${response.statusText}`);
      }

      return response.json() as Promise<DashboardResponse>;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}

// Public registration mutation (no auth required)

interface CreateRegistrationParams {
  name: string;
  surname: string;
  phone: string;
  email: string;
  age?: number;
  gender?: string;
  expoVersion?: string;
  expoData?: Record<string, unknown>;
}

interface CreateRegistrationResponse {
  id: number;
  uuid: string;
}

interface RegistrationErrorResponse {
  message: string;
  code?: "PHONE_EXISTS" | "EMAIL_EXISTS";
  field?: "phone" | "email";
}

export function useCreateRegistration() {
  return useMutation({
    mutationFn: async (data: CreateRegistrationParams) => {
      const response = await fetch("/api/registrations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as RegistrationErrorResponse;
        const error = new Error(errorData.message || "Failed to create registration") as Error & {
          code?: string;
          field?: string;
          status?: number;
        };
        error.code = errorData.code;
        error.field = errorData.field;
        error.status = response.status;
        throw error;
      }

      return response.json() as Promise<CreateRegistrationResponse>;
    },
  });
}
